
import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import type { Chef, Language, Recipe, User } from '../types';
import XIcon from './icons/XIcon';
import SearchField from './SearchField';
import { getTranslation, isRTL } from '../utils/translations';
import { getChefDisplayName, getChefsWithRecipes } from '../utils/chefDisplay';

interface ChefFilterModalProps {
    isOpen: boolean;
    onClose: () => void;
    chefs: Chef[];
    recipes: Recipe[];
    users?: User[];
    selectedChefIds: string[];
    onApply: (chefIds: string[]) => void;
    language?: Language;
}

const ChefFilterModal: React.FC<ChefFilterModalProps> = ({
    isOpen, onClose, chefs, recipes, users = [], selectedChefIds, onApply, language = 'en',
}) => {
    const t = (key: any) => getTranslation(language, key);
    const rtl = isRTL(language);
    const textDir = rtl ? 'rtl' : 'ltr';

    const [selected, setSelected] = useState<string[]>(selectedChefIds);
    const [query, setQuery] = useState('');
    const [initialSelected, setInitialSelected] = useState<Set<string>>(new Set(selectedChefIds));

    useEffect(() => {
        if (isOpen) {
            setSelected(selectedChefIds);
            setInitialSelected(new Set(selectedChefIds));
            setQuery('');
        }
    }, [isOpen]);

    const chefEntries = useMemo(
        () => {
            const entries = getChefsWithRecipes(chefs, recipes, users);
            return [...entries].sort((a, b) => {
                const aSel = initialSelected.has(a.chef.id) ? 1 : 0;
                const bSel = initialSelected.has(b.chef.id) ? 1 : 0;
                if (aSel !== bSel) return bSel - aSel;
                return b.recipeCount - a.recipeCount;
            });
        },
        [chefs, recipes, users, initialSelected]
    );

    const filteredEntries = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return chefEntries;
        return chefEntries.filter(({ chef }) =>
            getChefDisplayName(chef, language).toLowerCase().includes(q)
            || chef.englishName.toLowerCase().includes(q)
            || (chef.name?.toLowerCase().includes(q) ?? false)
        );
    }, [chefEntries, query, language]);

    if (!isOpen) return null;

    const toggleChef = (chefId: string) => {
        setSelected(prev => prev.includes(chefId) ? prev.filter(id => id !== chefId) : [...prev, chefId]);
    };

    const handleApply = () => {
        onApply(selected);
        onClose();
    };

    const titleBlock = (
        <div className="text-start min-w-0">
            <h3 className="text-xl font-black text-slate-800 dark:text-slate-100 tracking-tight flex items-center gap-2">
                {t('filterByChef')}
                {selected.length > 0 && (
                    <span className="px-2 py-0.5 bg-brand-sage text-white text-[10px] font-black rounded-full shrink-0">
                        {selected.length}
                    </span>
                )}
            </h3>
            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mt-0.5">{t('chefsWithRecipes')}</p>
        </div>
    );

    const closeButton = (
        <button
            onClick={onClose}
            className="p-2 text-slate-300 dark:text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 transition-colors bg-slate-50 dark:bg-slate-800 rounded-full shrink-0"
        >
            <XIcon className="w-6 h-6" />
        </button>
    );

    const clearButton = (
        <button
            onClick={() => setSelected([])}
            className="flex-1 py-3.5 text-slate-400 dark:text-slate-500 font-black text-xs uppercase tracking-widest hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
        >
            {t('clearAll')}
        </button>
    );

    const saveButton = (
        <button
            onClick={handleApply}
            className="flex-[2] py-3.5 bg-brand-orange text-white font-black rounded-2xl shadow-xl shadow-brand-orange/20 hover:opacity-90 active:scale-95 transition-all text-sm uppercase tracking-widest"
        >
            {t('save')}
        </button>
    );

    return createPortal(
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[510] flex items-center justify-center p-4 animate-fade-in"
            onClick={onClose}
        >
            <div
                className="bg-white dark:bg-slate-900 w-full max-w-md max-h-[min(85dvh,calc(100dvh-2rem))] rounded-[2.5rem] shadow-2xl flex flex-col overflow-hidden border border-white/20 dark:border-slate-800 transition-colors"
                dir={textDir}
                onClick={e => e.stopPropagation()}
            >
                {/* Header: dir=rtl places title right, close left */}
                <div className="px-6 pt-6 pb-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-white dark:bg-slate-900 shrink-0">
                    {titleBlock}
                    {closeButton}
                </div>

                {/* Search */}
                {chefEntries.length > 0 && (
                    <div className="px-6 pt-4 shrink-0">
                        <SearchField
                            value={query}
                            onChange={setQuery}
                            placeholder={t('searchChefPlaceholder')}
                            clearLabel={t('clearSearch')}
                            dir={textDir}
                            inputClassName="py-2.5 rounded-xl focus:ring-sky-500"
                        />
                    </div>
                )}

                {/* Chef list */}
                <div className="flex-grow overflow-y-auto scrollbar-none px-6 py-4">
                    {filteredEntries.length === 0 ? (
                        <p className="text-center text-sm text-slate-400 dark:text-slate-500 py-10 font-bold">
                            {t('noResults')}
                        </p>
                    ) : (
                        <div className="space-y-2">
                            {filteredEntries.map(({ chef, recipeCount }) => {
                                const isChecked = selected.includes(chef.id);
                                const checkbox = (
                                    <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-all
                                        ${isChecked ? 'bg-brand-sage border-brand-sage' : 'border-slate-300 dark:border-slate-600'}`}>
                                        {isChecked && (
                                            <svg className="w-3 h-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0L3.3 9.7a1 1 0 0 1 1.4-1.4l3.3 3.3 6.8-6.8a1 1 0 0 1 1.4 0z" clipRule="evenodd" />
                                            </svg>
                                        )}
                                    </span>
                                );
                                const countBadge = (
                                    <span className={`text-[11px] font-black shrink-0 px-2 py-0.5 rounded-full
                                        ${isChecked ? 'bg-brand-sage/20 text-brand-sage' : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'}`}>
                                        {recipeCount}
                                    </span>
                                );

                                return (
                                    <button
                                        key={chef.id}
                                        type="button"
                                        onClick={() => toggleChef(chef.id)}
                                        dir="ltr"
                                        className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-2xl border transition-all
                                            ${isChecked
                                                ? 'bg-brand-sage/10 border-brand-sage'
                                                : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'}`}
                                    >
                                        {rtl ? (
                                            <>
                                                {countBadge}
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <span dir={textDir} className="font-bold text-sm text-slate-800 dark:text-slate-100 truncate">
                                                        {getChefDisplayName(chef, language)}
                                                    </span>
                                                    {checkbox}
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="flex items-center gap-3 min-w-0">
                                                    {checkbox}
                                                    <span className="font-bold text-sm text-slate-800 dark:text-slate-100 truncate">
                                                        {getChefDisplayName(chef, language)}
                                                    </span>
                                                </div>
                                                {countBadge}
                                            </>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex gap-4 shrink-0">
                    {rtl ? (
                        <>
                            {saveButton}
                            {clearButton}
                        </>
                    ) : (
                        <>
                            {clearButton}
                            {saveButton}
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default ChefFilterModal;
